import React, { useCallback, useRef, useState } from 'react';
import { TableProps, TableScrollState } from '../../../types/DesignSystem/Table';
import TableHeader from './TableHeader';
import TableRow from './TableRow';
import TableFooter from './TableFooter';
import Skeleton from '../Skeleton/Skeleton';
import RefreshOverlay from './RefreshOverlay';
import EmptyState from '../EmptyState/EmptyState';
import Checkbox from '../Checkbox/Checkbox';
import { stickyBodyCellClasses, stickyCellGround, stickyEdgeShadow } from './stickyColumn';
import { TableDensity } from '../../../types/DesignSystem/Table';
import { useDensity } from '../DensityProvider/DensityProvider';
import { withClassName } from '../cn';
import { useMergedRef } from '../useMergedRef';
import { LiveRegionWords } from '../liveRegion';

/* § 245 — the skeleton row keeps the REAL row height at each density (12px bar +
   padding = the 20px line box + its padding), so data landing moves nothing. */
const SKELETON_PY: Record<TableDensity, string> = {
  compact: 'tw-py-2.5',
  default: 'tw-py-3',
  comfortable: 'tw-py-4',
};

/**
 * Get row key from row data
 */
const getRowKey = <T,>(
  row: T,
  rowKey: TableProps<T>['rowKey'],
  index: number
): string | number => {
  if (typeof rowKey === 'function') {
    return rowKey(row);
  }
  const key = row[rowKey as keyof T];
  return key !== undefined && key !== null ? String(key) : index;
};

const TableInner = <T,>({
  columns,
  data,
  rowKey,
  headerVariant,
  density: densityProp,
  striped = false,
  hoverable = false,
  bordered = true,
  stickyHeader = false,
  maxHeight,
  sortState,
  onSort,
  onRowClick,
  emptyContent,
  loading = false,
  footer,
  className = '',
  id,
  'data-testid': dataTestId,
  renderExpandedRow,
  expandedRowKeys,
  onExpandChange,
  isRowExpandable,
  isRowSunken,
  expandLabel,
  isRowExpandLoading,
  selectable = false,
  selectedKeys,
  onSelectionChange,
  isRowSelectable,
  selectionLabel,
  selectAllLabel = 'Select all rows',
  rowActionHint = 'Press Enter to open',
  caption,
  onScroll: onScrollProp,
  style: styleProp,
  ...rest
}: TableProps<T>, forwardedRef: React.ForwardedRef<HTMLDivElement>) => {
  /* § 313 — one density signal, not two. § 245 gave this component its own prop and
     nothing else in the system had one; a DensityProvider now sets the same word for a
     whole region, so a compact toolbar and the compact grid under it agree without the
     caller stating it twice. An explicit prop still wins — density is a default. */
  const inheritedDensity = useDensity();
  const density = densityProp ?? inheritedDensity;
  /*
    Expansion is opt-in by SHAPE, not by a boolean: supplying `renderExpandedRow` is
    what turns the column on. A separate `expandable` flag would let a caller declare
    expansion and render nothing, and then the grid grows a column of dead chevrons.
  */
  const expandable = typeof renderExpandedRow === 'function';
  /*
    § 245 — the head's default ground follows the ZEBRA. A plain table takes the quiet
    `light` head (surface-sunken): a header labels the data and sits behind it. But the
    stripe IS surface-sunken, so on a striped table (or an `isRowSunken` group zebra)
    a light head lands on the stripe's own colour and reads as one more row — caught by
    the user on the showcase's Striped Table the day § 245 landed. Those tables keep the
    `dark` band, which restores the three-tier ladder they always had: band > stripe >
    row. An explicit `headerVariant` always wins.
  */
  const zebra = striped || typeof isRowSunken === 'function';
  const resolvedHeaderVariant = headerVariant ?? (zebra ? 'dark' : 'light');
  const expandedKeys = new Set(expandedRowKeys ?? []);
  const columnCount = columns.length + (expandable ? 1 : 0) + (selectable ? 1 : 0);

  /*
    § 299 — selection is CONTROLLED, like sort and expansion: the table derives what to
    draw from `selectedKeys` and reports the next complete array. The header box works
    on THIS page's selectable rows only, and keeps every key that is not on this page —
    a paged consumer can accumulate a selection across pages without the table knowing.
  */
  const selectedSet = new Set(selectedKeys ?? []);
  const pageKeys = selectable
    ? data
      .filter((row) => (isRowSelectable ? isRowSelectable(row) : true))
      .map((row, index) => getRowKey(row, rowKey, index))
    : [];
  const pageOn = pageKeys.filter((key) => selectedSet.has(key)).length;
  const allOn = pageKeys.length > 0 && pageOn === pageKeys.length;
  const someOn = pageOn > 0 && !allOn;
  const toggleAll = () => {
    if (!onSelectionChange) return;
    const pageSet = new Set(pageKeys);
    const others = (selectedKeys ?? []).filter((key) => !pageSet.has(key));
    onSelectionChange(allOn ? others : [...others, ...pageKeys]);
  };
  const toggleOne = (key: string | number) => {
    if (!onSelectionChange) return;
    const current = selectedKeys ?? [];
    onSelectionChange(selectedSet.has(key) ? current.filter((k) => k !== key) : [...current, key]);
  };
  const selectionCell = selectable ? (
    <Checkbox
      label=""
      aria-label={selectAllLabel}
      checked={allOn}
      indeterminate={someOn}
      disabled={pageKeys.length === 0}
      onChange={toggleAll}
      data-testid={dataTestId ? `${dataTestId}-select-all` : undefined}
    />
  ) : undefined;

  /*
    Scroll-aware chrome (§ 222) — the Dialog § 53 signature, for the two scrolls a
    table has. `under` puts `sm` beneath a pinned header once rows have slid under
    it; `left`/`right` turn a pinned column's hairline seam into a LAYER (the
    sideways `sm`) while columns are actually sliding beneath the pin. Measured
    from the one scrolling wrapper; the ResizeObserver covers content growing
    without a scroll event (rows landing, columns widening). jsdom has neither
    layout nor ResizeObserver, so everything rests at false there — which is also
    the at-rest truth in a browser.
  */
  /*
    § 349 — the clickable rows are ONE tab stop. The active row holds `tabIndex` 0 and
    follows focus (arrow keys, a click, a programmatic focus); until one is focused, or
    when the active row leaves `data`, the first row holds it.
  */
  const [activeRowKey, setActiveRowKey] = useState<string | number | null>(null);
  const rowsClickable = typeof onRowClick === 'function';
  /* § 380 — a clickable row is a focusable `<tr>` with no widget role, so nothing told a screen
     reader that Enter does something; every one is described by this one hidden sentence. */
  const rowHintId = `${React.useId()}-row-action`;
  const rowKeys = rowsClickable ? data.map((row, index) => getRowKey(row, rowKey, index)) : [];
  const tabStopKey = activeRowKey !== null && rowKeys.includes(activeRowKey) ? activeRowKey : rowKeys[0];

  const wrapRef = useRef<HTMLDivElement>(null);
  /* § 384 — the caller's ref reaches the region too; `wrapRef` stays the scroll measure. */
  const setWrapRef = useMergedRef<HTMLDivElement>(wrapRef, forwardedRef);
  const [scrolled, setScrolled] = useState<TableScrollState>({ under: false, left: false, right: false });
  const measureScroll = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const under = el.scrollTop > 0;
    const left = el.scrollLeft > 0;
    /* −1: fractional scroll positions on zoomed pages — the Dialog footer lesson. */
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setScrolled((prev) =>
      prev.under === under && prev.left === left && prev.right === right
        ? prev
        : { under, left, right },
    );
  }, []);
  /*
    § 380 — a pinned head must never cover the focused row. The browser scrolls a focused
    element to the scroller's top edge, and that is where `stickyHeader` pins the head: in
    Chromium a row arrived at 0.5px under a header spanning 1–37px, all but hidden (WCAG 2.2 SC
    2.4.11). `scroll-padding-top` equal to the head's height moves the edge below it, and focus
    scrolling honours it. Measured, because the head's height follows density and wrapping;
    re-measured by the ResizeObserver below. Zero (jsdom, no head) sets nothing.
  */
  const [headHeight, setHeadHeight] = useState(0);
  const measureHead = useCallback(() => {
    if (!stickyHeader) return;
    const head = wrapRef.current?.querySelector('thead');
    const height = head ? Math.round(head.getBoundingClientRect().height) : 0;
    setHeadHeight((prev) => (prev === height ? prev : height));
  }, [stickyHeader]);
  React.useLayoutEffect(() => {
    measureScroll();
    measureHead();
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      measureScroll();
      measureHead();
    });
    observer.observe(el);
    /* The wrapper only resizes with the viewport; the TABLE resizes when data
       lands or columns change — observing both is what keeps `right` honest. */
    const table = el.querySelector('table');
    if (table) observer.observe(table);
    return () => observer.disconnect();
  }, [measureScroll, measureHead]);
  const tableBase = 'tw-w-full tw-border-collapse';
  /**
   * **The border and the radius live on the WRAPPER, not on the `<table>`.**
   *
   * They were on the table, as `tw-border tw-border-grey-200 tw-rounded-srf
   * tw-overflow-hidden`, and the computed style confirmed all four sides at 1px
   * `grey-200` with a 10px radius. **None of the vertical borders were painted.**
   * Measured in headless Chromium at 4x on the real rendered component: the left edge
   * showed the header's rounded grey fill, the row rules, the footer tint — and no
   * vertical line anywhere.
   *
   * The cause is `border-collapse: collapse`. In the collapsing border model the
   * table's own border is resolved against the edge cells' borders, and CSS Backgrounds
   * &amp; Borders § 5.3 states plainly that `border-radius` does **not** apply to a table
   * element with collapsed borders. Chrome honours the radius for the *background*
   * clip — which is why the header corner looks rounded — and drops the collapsed
   * border. So the "bordered" table had horizontal rules from `TableRow`'s `border-b`
   * and nothing else, and `tw-rounded-srf` was dead CSS on that element.
   *
   * A wrapper `<div>` is a plain block box: its four borders always paint and its radius
   * always applies. The wrapper is already a clipping container — `overflow-x-auto`
   * computes `overflow-y` to `auto` too, verified as `auto/auto` — so the corners clip
   * without adding `overflow-hidden`, which would have killed horizontal scrolling on a
   * wide table.
   *
   * This does not invalidate `FOCUS_RING_INSET` in `TableHeader`: the wrapper still
   * clips, so a ring drawn outside a header cell is still cut off at the table edge.
   */
  const borderedClass = bordered ? 'tw-border tw-border-edge tw-rounded-srf' : '';

  const isEmpty = data.length === 0;
  /** First load: hold the row heights so the table does not jump when data lands. */
  const showSkeleton = loading && isEmpty;
  const SKELETON_ROWS = 5;

  return (
    <div
      /* § 384 — rest first (the Button.tsx rule); the region's own wiring below wins. */
      {...rest}
      id={id}
      ref={setWrapRef}
      onScroll={(event) => {
        measureScroll();
        onScrollProp?.(event);
      }}
      /* `maxHeight` is what turns this wrapper into the scrolling ancestor a pinned
         header resolves against. Without it, `stickyHeader` has nothing to stick to. */
      /*
       * **Overscroll is contained PER AXIS, and only on an axis this element can
       * actually scroll.** § 222 contained both, and that killed page scrolling over
       * every table in the app: put the cursor on one and the wheel did nothing.
       *
       * The mechanism chains three facts together.
       *   1. With `overflow-x: auto`, the used value of `overflow-y` is `auto` too —
       *      the same promotion the border docblock above relies on for clipping. So
       *      this wrapper is a scroll container on BOTH axes.
       *   2. A table wide enough to scroll sideways has a real scrollable area, which
       *      is what makes Chrome treat it as the scroll node under the cursor. (A
       *      table that fits is not promoted, which is why this looked intermittent —
       *      it reproduces only once the table overflows horizontally.)
       *   3. It has no vertical range to absorb a vertical wheel, so the only way for
       *      that wheel to do anything is to chain to the page — and `contain` is
       *      precisely the instruction not to.
       *
       * Measured on `/gms/guide` at a width where the table overflows: wheel over it,
       * `contain` moved the page 0px and `auto` moved it 400px.
       *
       * So: contain X always — this really is the horizontal scroller — and contain Y
       * only once `maxHeight` has made it a vertical scroller too. That keeps what
       * § 222 wanted (a swipe past the last column, or a wheel at the end of a bounded
       * table's rows, stops here) without claiming a scroll this element cannot
       * perform.
       */
      className={withClassName(`tw-relative tw-overflow-x-auto tw-overscroll-x-contain ${maxHeight ? 'tw-overflow-y-auto tw-overscroll-y-contain' : ''} ${borderedClass}`, className)}
      style={maxHeight
        ? { ...styleProp, maxHeight, ...(stickyHeader && headHeight > 0 ? { scrollPaddingTop: headHeight } : {}) }
        : styleProp}
      data-testid={dataTestId}
      aria-busy={loading || undefined}
    >
      {/* One live region for the wait, whichever visual is showing. The skeletons
          are `aria-hidden`, so without this a first load is announced not at all. */}
      {rowsClickable && (
        <span id={rowHintId} className="tw-sr-only" data-testid={dataTestId ? `${dataTestId}-row-action-hint` : undefined}>
          {rowActionHint}
        </span>
      )}
      {showSkeleton && (
        <span role="status" className="tw-sr-only" data-testid={dataTestId ? `${dataTestId}-loading` : undefined}>
          {/* § 396 — the region mounts with the skeleton, so its word waits for it. */}
          <LiveRegionWords>Loading</LiveRegionWords>
        </span>
      )}
      {/*
        The overlay is for a REFRESH — rows already on screen, new ones on the way.
        A first load has nothing to dim, so it renders skeleton rows in the body
        instead (see `showSkeleton` below). Previously `loading` always drew this
        overlay, so a first load showed "Loading..." on top of the empty state's
        "No data available": two contradictory answers to the same question, at
        once. The two states are now mutually exclusive.
      */}
      {/* Extracted to `RefreshOverlay` so the hand-rolled tables that cannot use this
          component — Catalog Management's seven, which need `colgroup` widths and a
          split header/body scroll pair — show the same chip rather than a second
          implementation of it. All the reasoning lives in its docblock. */}
      <RefreshOverlay
        active={loading && !isEmpty}
        data-testid={dataTestId ? `${dataTestId}-loading` : undefined}
      />

      <table className={`${tableBase}${loading && !isEmpty ? ' tw-opacity-40' : ''}`}>
        {/* § 302 — the table's accessible NAME. A <caption>, visually hidden: the
            HTML way to name a table, read by every screen reader as the table is
            entered ("Products, table, 6 columns"), where `aria-label` on a <table>
            is honoured unevenly. Absent when the caller passes none — additive. */}
        {caption && <caption className="tw-sr-only">{caption}</caption>}
        <TableHeader
          columns={columns}
          variant={resolvedHeaderVariant}
          density={density}
          stickyHeader={stickyHeader}
          sortState={sortState}
          onSort={onSort}
          expandable={expandable}
          selectionCell={selectionCell}
          scrolled={scrolled}
          data-testid={dataTestId}
        />

        <tbody>
          {showSkeleton ? (
            Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
              <tr key={`skeleton-${rowIndex}`} data-testid={dataTestId ? `${dataTestId}-skeleton-row` : undefined}>
                {/* § 299 — the selection column exists while loading too, or the grid
                    narrows by one and every header shifts when data lands. */}
                {selectable && <td className={`tw-px-3 ${SKELETON_PY[density]} tw-bg-surface tw-w-12`} />}
                {/* The skeleton keeps the pinning: a first load that renders
                    unpinned columns and then re-renders them pinned shifts the
                    whole grid sideways the instant data lands. */}
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={`tw-px-3 ${SKELETON_PY[density]} tw-bg-surface ${stickyBodyCellClasses(
                      column.sticky,
                    )} ${stickyEdgeShadow(column.sticky, scrolled)} ${stickyCellGround(column.sticky, { striped: false, hoverable: false })}`}
                  >
                    {/* `wave` staggers the shimmer's phase per row (§ 223), so the
                        loading table reads as ONE light band sweeping it rather
                        than five synchronized strips. */}
                    <Skeleton variant="text" wave={rowIndex} />
                  </td>
                ))}
                {/* The expand column exists while loading too, or the grid narrows by
                    one and every header shifts the instant data lands. */}
                {expandable && <td className={`tw-px-3 ${SKELETON_PY[density]} tw-bg-surface`} />}
              </tr>
            ))
          ) : isEmpty ? (
            <tr>
              <td
                colSpan={columnCount}
                data-testid={dataTestId ? `${dataTestId}-empty` : undefined}
                className="tw-bg-surface"
              >
                {/* `emptyContent` still wins outright — a caller who knows WHY their
                    table is empty can say so far better than a shared default. */}
                {emptyContent ?? (
                  <EmptyState
                    title="No data available"
                    size="sm"
                    data-testid={dataTestId ? `${dataTestId}-empty-state` : undefined}
                  />
                )}
              </td>
            </tr>
          ) : (
            data.map((row, index) => {
              const key = getRowKey(row, rowKey, index);
              const canExpand = expandable && (isRowExpandable ? isRowExpandable(row) : true);
              const isExpanded = expandedKeys.has(key);
              return (
                <TableRow
                  key={key}
                  row={row}
                  rowIndex={index}
                  columns={columns}
                  hoverable={hoverable}
                  striped={striped}
                  sunken={isRowSunken ? isRowSunken(row, index) : undefined}
                  density={density}
                  scrolled={scrolled}
                  onClick={onRowClick}
                  data-testid={dataTestId ? `${dataTestId}-row-${key}` : undefined}
                  expandable={expandable}
                  expanded={isExpanded}
                  canExpand={canExpand}
                  expandLoading={canExpand && Boolean(isRowExpandLoading?.(row))}
                  onToggleExpand={() => onExpandChange?.(key, !isExpanded)}
                  expandLabel={
                    expandLabel
                      ? expandLabel(row, isExpanded)
                      : isExpanded ? 'Collapse row' : 'Expand row'
                  }
                  /* Rendered only while open: the caller's content may be expensive
                     (a nested Table over fetched children), and a closed row must not
                     pay for it. */
                  expandedContent={isExpanded && canExpand ? renderExpandedRow?.(row, index) : null}
                  selectable={selectable}
                  selected={selectable && selectedSet.has(key)}
                  canSelect={isRowSelectable ? isRowSelectable(row) : true}
                  onToggleSelect={() => toggleOne(key)}
                  selectionLabel={selectionLabel ? selectionLabel(row) : undefined}
                  rowTabIndex={rowsClickable ? (key === tabStopKey ? 0 : -1) : undefined}
                  onRowFocus={rowsClickable ? () => setActiveRowKey(key) : undefined}
                  actionHintId={rowsClickable ? rowHintId : undefined}
                />
              );
            })
          )}
        </tbody>

        {footer && <TableFooter>{footer}</TableFooter>}
      </table>
    </div>
  );
};

/* § 384 — `forwardRef` drops a generic component's type parameter; the cast restores `T`. */
const Table = React.forwardRef(TableInner) as <T>(
  props: TableProps<T> & React.RefAttributes<HTMLDivElement>,
) => React.ReactElement | null;

export default Table;
